<template>
  <div class="Double-range__container">
    <div :class="['Double-range__slider', { 'Double-range__slider--disabled': disabled }]">
      <div class="Double-range__tooltip-container">
        <div class="Double-range__tooltip-subcontainer">
          <span :style="minThumbStyle" class="Double-range__thumb-label">{{ minThumbTooltip }}</span>
          <span :style="maxThumbStyle" class="Double-range__thumb-label">{{ maxThumbTooltip }}</span>
        </div>
      </div>

      <input
        :aria-label="$t('ux.atoms.form.doubleRange.ariaMinimum')"
        :class="[{ 'Double-range__slider-to': firstIsOnTop }]"
        :disabled="disabled"
        :max="max"
        :min="min"
        @input="controlMinInput"
        ref="fromSlider"
        type="range"
        v-model="currentMin"
      />
      <input
        :aria-label="$t('ux.atoms.form.doubleRange.ariaMaximum')"
        :class="[{ 'Double-range__slider-to': secondIsOnTop }]"
        :disabled="disabled"
        :max="max"
        :min="min"
        @input="controlMaxInput"
        ref="toSlider"
        type="range"
        v-model="currentMax"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { fillSlider, getThumbPosition } from '~/helpers/forms';

interface DoubleRangeProps {
  currency: string;
  defaultCurrentMax: number;
  defaultCurrentMin: number;
  disabled: boolean;
  max: number;
  min: number;
  modelCurrentMax: number;
  modelCurrentMin: number;
}

interface Emits {
  (e: 'update:modelCurrentMax', value: number): void;
  (e: 'update:modelCurrentMin', value: number): void;
}

const emit = defineEmits<Emits>();

const fromSlider = ref<HTMLInputElement | null>(null);
const toSlider = ref<HTMLInputElement | null>(null);

const props = withDefaults(defineProps<DoubleRangeProps>(), {
  currency: '',
  disabled: false,
  max: 200,
  min: 0
});

const currentMin = ref<string>(props.modelCurrentMin.toString());
const currentMax = ref<string>(props.modelCurrentMax.toString());

const firstIsOnTop = computed(() => {
  return Number(currentMin.value) === Number(currentMax.value) && Number(currentMin.value) !== props.min;
});

const secondIsOnTop = computed(() => {
  return Number(currentMin.value) === props.min || Number(currentMin.value) !== Number(currentMax.value);
});

const minThumbStyle = computed(() => {
  if (fromSlider.value) {
    const position = getThumbPosition(Number(currentMin.value), fromSlider.value);
    return {
      left: `${position}%`
    };
  }
  return {};
});

const maxThumbStyle = computed(() => {
  if (toSlider.value) {
    const position = getThumbPosition(Number(currentMax.value), toSlider.value);
    return {
      left: `${position}%`
    };
  }
  return {};
});

const minThumbTooltip = computed(() => {
  return `${currentMin.value} ${props.currency}`;
});

const maxThumbTooltip = computed(() => {
  return `${currentMax.value} ${props.currency}`;
});

const controlMinInput = (event: Event) => {
  const targetValue = Number((event.target as HTMLInputElement).value);
  if (targetValue > Number(currentMax.value)) {
    currentMin.value = currentMax.value;
  }
};

const controlMaxInput = (event: Event) => {
  const targetValue = Number((event.target as HTMLInputElement).value);
  if (targetValue < Number(currentMin.value)) {
    currentMax.value = currentMin.value;
  }
};

const clearInput = () => {
  currentMax.value = props.max.toString();
  currentMin.value = props.min.toString();
};

defineExpose({
  clearInput
});

watch(
  () => currentMin.value,
  useDebounceFn((value) => {
    emit('update:modelCurrentMin', Number(value));
  }, 300)
);

watch(
  () => currentMax.value,
  useDebounceFn((value) => {
    emit('update:modelCurrentMax', Number(value));
  }, 300)
);

watchEffect(() => {
  if (toSlider.value) {
    fillSlider(Number(currentMin.value), Number(currentMax.value), fromSlider);
  }
});

onMounted(() => {
  clearInput();
});
</script>

<style lang="scss" scoped>
@use 'DoubleRange.scss';
</style>
